/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {computed, DOCUMENT, Component, Inject, OnDestroy, signal} from '@angular/core';
import {UIRouter} from '@uirouter/core';
import * as _ from 'lodash';
import * as moment from 'moment';

import template from './base-shell-layout.html';
import BUILD_INFO from '../../gen-config/build-info';
import {AUTH_EVENT, PERMISSIONS} from '../../components/auth/auth-constants';
import {ApiConfig} from '../config/api-config';
import {getRegisteredUrl} from '../config/registered-urls';
import {SESSION_EVENT} from '../common/constants/misc-constants';
import {AppShellStateService} from './app-shell-state.service';
import {ReleaseNotesHelper} from '../../components/util/release-notes-helper.service';
import {ReleaseNotesModalService} from './simple/release-notes-modal.service';
import {UserProfileApiService} from '../admin/user-profile/user-profile-api.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {LocalStorage} from '../../components/session/local-storage';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {BrowserLocationService, WINDOW} from '../angular-platform-providers';
import {deferSignalStateWrite} from '../common/util/template-state';
import {AppEventBusService} from '../common/events/app-event-bus.service';
import {ruiOriginForUiiHostname, translateUiiPathToRui} from './rui-url';

const RUI_SWITCH_SESSION_KEY = 'ruiSwitchAvailable';
const ALWAYS_SHOW_RUI_SWITCH_USERS = ['adnuntius@adnuntius.com', 'broker1@adnuntius.com'];

@Component({
  standalone: false,
  selector: 'adn-base-shell-layout',
  template
})
export class BaseShellLayoutComponent implements OnDestroy {
  readonly masq = signal<any>({});
  readonly isSelfServiceOwn = signal(false);
  readonly specialPanelVisible = signal(false);
  readonly showNetworks = signal(false);
  readonly showNotifications = signal(false);
  readonly showLast = signal(false);
  ignoreClearOut = false;
  readonly animationClass = signal('');
  readonly showReleaseNotesNotification = signal(false);
  readonly permissions = signal<any>({});
  readonly env = signal('');
  readonly uiLogo = signal('');
  readonly releaseId = signal('');
  readonly showRuiSwitch = signal(false);
  readonly ruiUrl = signal('');
  readonly appNetworkCount = computed(() => this.shell.appNetworks.length);

  private localUser: any;
  private deregisterTransition: () => void;
  private destroyNetworkChangeListener: () => void;
  private destroyUserProfileUpdateListener: () => void;
  private onDocumentClick: (event: MouseEvent) => void;
  private onDocumentKeydown: (event: KeyboardEvent) => void;
  private pageEnterClearTimer: ReturnType<typeof setTimeout> | undefined;
  private pageEnterRaf = 0;
  private ruiOrigin = '';

  constructor(
    readonly shell: AppShellStateService,
    private readonly eventBus: AppEventBusService,
    @Inject(BrowserLocationService) private readonly location: any,
    @Inject(WINDOW) private readonly window: any,
    @Inject(DOCUMENT) private readonly document: any,
    private readonly uiRouter: UIRouter,
    private readonly releaseNotesHelper: ReleaseNotesHelper,
    private readonly releaseNotesModalService: ReleaseNotesModalService,
    @Inject(UserProfileApiService) private readonly UserProfile: UserProfileApiService,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly apiConfig: ApiConfig,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    this.init();
  }

  toggleNotifications(): void {
    this.doToggling('showNotifications');
  }

  toggleLast(): void {
    this.doToggling('showLast');
  }

  toggleNetworks(): void {
    this.doToggling('showNetworks');
  }

  toggleChangelog(): void {
    this.releaseNotesModalService.open(this.getReleaseNoteApps());

    this.localUser = this.localUserProfile.get();
    this.localUser.profile.adnPrefs.lastReleaseNotesRead = moment().unix();
    firstValueFrom(this.UserProfile.saveProfile({
      id: this.localUser.userId,
      profile: {
        adnPrefs: this.localUser.profile.adnPrefs
      }
    })).then((apiUser: any) => {
      this.localUser = this.localUserProfile.safeSaveProfile('adnPrefs', apiUser.profile.adnPrefs);
      this.setReleaseNotesNotification(false);
    });
  }

  clearOut(): void {
    if (this.ignoreClearOut) {
      return;
    }
    this.emptyOut();
    this.evaluateVisibility();
  }

  ngOnDestroy(): void {
    if (this.deregisterTransition) {
      this.deregisterTransition();
    }
    if (this.destroyNetworkChangeListener) {
      this.destroyNetworkChangeListener();
    }
    if (this.destroyUserProfileUpdateListener) {
      this.destroyUserProfileUpdateListener();
    }
    if (this.onDocumentClick) {
      this.document.removeEventListener('click', this.onDocumentClick);
    }
    if (this.onDocumentKeydown) {
      this.document.removeEventListener('keydown', this.onDocumentKeydown);
    }
    this.clearPageEnterTimers();
  }

  private init(): void {
    const userPermissions = this.LocalUserPermissions.get();

    this.masq.set({
      supportUserId: this.localUserProfile.get().supportUserId,
      username: this.localUserProfile.get().username
    });
    this.isSelfServiceOwn.set(this.LocalUserPermissions.isSelfServiceOwn());

    const regUrl = getRegisteredUrl(this.window, this.apiConfig, this.localNetworkInfo, this.LocalNetworkProfile.get('networkId'));
    if (regUrl && regUrl.favicon) {
      const favicon = this.document.getElementById('favicon');
      if (favicon) {
        favicon.href = regUrl.favicon.href;
      }
    }

    this.shell.bootstrap();

    this.localUser = this.localUserProfile.get();
    if (!this.localUser) {
      return;
    }
    this.ruiOrigin = ruiOriginForUiiHostname(this.location.hostname) || '';
    this.updateRuiUrl();
    const arrivedFromRui = this.location.queryParams.source === 'rui';
    if (arrivedFromRui) {
      LocalStorage.obtain().setSessionItem(RUI_SWITCH_SESSION_KEY, true);
      const url = new URL(this.window.location.href);
      url.searchParams.delete('source');
      this.window.history.replaceState(this.window.history.state, '', url.toString());
    }
    const ruiSwitchAvailable = arrivedFromRui
      || LocalStorage.obtain().getSessionItem(RUI_SWITCH_SESSION_KEY) === true;
    this.showRuiSwitch.set(!!this.ruiOrigin
      && (ALWAYS_SHOW_RUI_SWITCH_USERS.includes(this.localUser.username) || ruiSwitchAvailable));

    // Do not set the badge true before the query — that true→false race is NG0100
    // on the changelog <a> when checkReleaseNotes finds nothing new.
    if (_.get(this.localUser, ['profile', 'adnPrefs', 'showReleaseHighlight'], 'SHOW') === 'SHOW') {
      this.checkReleaseNotes();
    }

    this.deregisterTransition = this.uiRouter.transitionService.onSuccess({}, () => {
      this.shell.refreshShellState();
      // UI-Router can update window.location during Angular's verification pass.
      // Cache the derived href on the next turn so one check sees one value.
      deferSignalStateWrite(() => {
        this.updateRuiUrl();
        this.playPageEnter();
      });
    }) as () => void;

    this.clearOut();

    if (_.startsWith(this.location.hostname, 'localhost') && !_.startsWith(this.apiConfig.getId(), 'localhost')) {
      this.env.set(this.apiConfig.getId().split('_')[0].toUpperCase());
    }

    this.permissions.set(_.mapValues(PERMISSIONS, (permission) => {
      return userPermissions && userPermissions.hasPermission(permission.apiKey);
    }));

    this.uiLogo.set(_.get(regUrl, ['logo']) || this.LocalNetworkProfile.get('adnUiLogo') || '');
    this.releaseId.set(BUILD_INFO.gitSha.substring(0, 10));

    this.destroyNetworkChangeListener = this.eventBus.on(AUTH_EVENT.networkChange, (_eventData: any, network: any) => {
      this.shell.syncCurrentNetworkFromAuthEvent(network);
      const updatedRegUrl = getRegisteredUrl(this.window, this.apiConfig, this.localNetworkInfo, network.id);
      this.uiLogo.set(_.get(updatedRegUrl, ['logo']) || this.LocalNetworkProfile.get('adnUiLogo') || '');
    });

    this.destroyUserProfileUpdateListener = this.eventBus.on(SESSION_EVENT.userProfileUpdate, () => {
      this.localUser = this.localUserProfile.get();
      const newReleaseHighlight = _.get(this.localUser, ['profile', 'adnPrefs', 'showReleaseHighlight'], 'SHOW');
      if (newReleaseHighlight === 'HIDE' && this.showReleaseNotesNotification()) {
        this.setReleaseNotesNotification(false);
      }
      if (newReleaseHighlight === 'SHOW' && !this.showReleaseNotesNotification()) {
        this.checkReleaseNotes();
      }
    });

    this.onDocumentClick = (event: MouseEvent) => {
      // ignoreClearOut covers the same click that opened/switched the panel
      // (that click bubbles here before *ngIf has painted .specialPanel).
      if (!this.specialPanelVisible() || this.ignoreClearOut) {
        return;
      }
      const panel = this.document.querySelector('.specialPanel');
      if (!panel || panel.contains(event.target as Node)) {
        return;
      }
      this.clearOut();
    };
    this.onDocumentKeydown = (event: KeyboardEvent) => {
      if (event.keyCode === 27 && this.specialPanelVisible()) {
        this.clearOut();
      }
    };

    this.document.addEventListener('click', this.onDocumentClick);
    this.document.addEventListener('keydown', this.onDocumentKeydown);
  }

  private getReleaseNoteApps(): string[] {
    const tags = [];
    if (this.LocalNetworkProfile.isMarketplacePlatform()) {
      tags.push('MARKETPLACE');
    }
    if (!this.LocalNetworkProfile.isPureMarketplacePlatform()) {
      tags.push('ADSERVER');
    }
    return tags;
  }

  private checkReleaseNotes(): void {
    const lastRead = _.get(this.localUser, ['profile', 'adnPrefs', 'lastReleaseNotesRead']);
    this.releaseNotesHelper.query(this.getReleaseNoteApps(), true, 'ACTIVE', lastRead).then((data: any[]) => {
      this.setReleaseNotesNotification(data.length > 0);
    });
  }

  /** Badge *ngIf must not flip during an in-flight CD verify pass (NG0100). */
  private setReleaseNotesNotification(visible: boolean): void {
    deferSignalStateWrite(() => this.showReleaseNotesNotification.set(visible));
  }

  private updateRuiUrl(): void {
    if (!this.ruiOrigin) {
      this.ruiUrl.set('');
      return;
    }
    const destination = translateUiiPathToRui(
      this.location.pathname,
      this.localUserProfile.getCurrentApp()
    );
    this.ruiUrl.set(new URL(destination, this.ruiOrigin).toString());
  }

  private emptyOut(): void {
    this.specialPanelVisible.set(false);
    this.showNetworks.set(false);
    this.showNotifications.set(false);
    this.showLast.set(false);
  }

  private evaluateVisibility(): void {
    this.specialPanelVisible.set(this.showNetworks() || this.showNotifications() || this.showLast());
  }

  private doToggling(param: 'showNetworks' | 'showNotifications' | 'showLast'): void {
    const toggles = {
      showNetworks: this.showNetworks,
      showNotifications: this.showNotifications,
      showLast: this.showLast
    };
    const toggle = toggles[param];
    if (this.specialPanelVisible() && !toggle()) {
      this.ignoreClearOut = true;
    }
    if (toggle()) {
      this.emptyOut();
      return;
    }
    this.ignoreClearOut = true;
    this.emptyOut();
    toggle.set(true);
    this.evaluateVisibility();
    // Keep ignoreClearOut through the opening click's document bubble, then allow dismiss.
    setTimeout(() => {
      this.ignoreClearOut = false;
    });
  }

  /** Re-trigger CSS enter animation after each successful route change. */
  private playPageEnter(): void {
    this.clearPageEnterTimers();
    this.animationClass.set('');
    this.pageEnterRaf = requestAnimationFrame(() => {
      this.animationClass.set('adn-page-enter');
      this.pageEnterClearTimer = setTimeout(() => {
        if (this.animationClass() === 'adn-page-enter') {
          this.animationClass.set('');
        }
      }, 160);
    });
  }

  private clearPageEnterTimers(): void {
    if (this.pageEnterRaf) {
      cancelAnimationFrame(this.pageEnterRaf);
      this.pageEnterRaf = 0;
    }
    if (this.pageEnterClearTimer) {
      clearTimeout(this.pageEnterClearTimer);
      this.pageEnterClearTimer = undefined;
    }
  }
}
